import { useEffect } from 'react'
import { Platform, ScrollView, Share } from 'react-native'

import { useRoute } from '@react-navigation/native'
import PdfDownloadLoader from '@zet/common/components/pdfDownloadLoader'
import { useToast } from '@zet/common/components/toast'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import useSharePdf from '@zet/common/hooks/useSharePdf'
import {
  B2,
  Button,
  H4,
  H5,
  Icon,
  Pressable,
  SH3,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import { i18n } from '@zet/localization'
import PDF from '@zet/svg/pdf'
import { LoggedInNavigation } from '@zet/utils/interface'
import { shareToApp } from '@zet/utils/shareLink'
import Lottie from 'lottie-react'
import LottieView from 'lottie-react-native'

import { useBrandDetailsById } from '../../api/brands/hooks'
import { useWebShare } from '../../hooks/useWebShare'
import useBrandStore from '../../store/brands'

const BrandShareDetails = () => {
  const { openWebShare, WebShare } = useWebShare()
  const { params } = useRoute()
  const brandId = params?.brandId
  const routeName = params?.routeName
  const { setLeadId } = useBrandStore((state) => state)

  const { navigate } = useNavigation<LoggedInNavigation>()
  const { data: content } = useBrandDetailsById(brandId || '')
  const { share, isLoading } = useSharePdf()
  const toast = useToast()

  const onShare = async (data: string) => {
    try {
      if (Platform.OS === 'web') {
        window.open(data, '_blank')
      } else await share(data)
    } catch (error) {
      console.warn(error)
    }
  }
  const PDFIcon = () => <PDF />

  useEffect(() => {
    setLeadId(params?.leadId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.leadId])

  useEffect(() => {
    // Todo: Fix this piece
    setTimeout(async () => {
      const shareContent = params?.shareConfig
      await openWebShare({
        ...shareContent,
        text: shareContent?.message,
        number: params?.customerPhoneNumber,
      })
    }, 1000)
  }, [openWebShare, params?.customerPhoneNumber, params?.shareConfig])

  const hanldeShare = async () => {
    try {
      if (Platform.OS === 'web') {
        window.open(
          `https://www.youtube.com/watch?v=${content?.applicationProcess?.videoLink}`,
          '_blank'
        )
      } else
        await Share.share({
          message: `https://www.youtube.com/watch?v=${content?.applicationProcess?.videoLink}`,
        })
    } catch (error) {
      console.warn(error)
    }
  }

  const handleReshare = async () => {
    const shareContent = params?.shareConfig
    if (Platform.OS === 'web') {
      await openWebShare({
        ...shareContent,
        text: shareContent?.message,
        number: params?.customerPhoneNumber,
      })
    } else {
      shareToApp(
        {
          isWhatsapp: false,
          base64Image: shareContent?.imageBase64,
          message: shareContent?.message,
          ...(params?.customerPhoneNumber
            ? { phoneNumber: params?.customerPhoneNumber }
            : {}),
        },
        toast
      )
    }
  }

  function handleLeadProgress() {
    navigate('LeadProgress', { leadId: params?.leadId })
  }
  const shareDetails = [
    {
      id: 'index-1',
      iconDetails: (
        <XStack className="items-center py-2">
          <Icon type="Youtube" size={45} />

          <SH3 className="ml-1">
            {i18n.t('brandShareDetails.process.video')}
          </SH3>
        </XStack>
      ),
      shareDetails: `https://www.youtube.com/watch?v=${content?.applicationProcess?.videoLink}`,
      eventPayload: { item_name: 'brands_share_material_youtube' },
      isRender: !!content?.applicationProcess?.videoLink,
      onPress: () => hanldeShare(),
    },
    {
      id: 'index-2',
      iconDetails: (
        <XStack className="items-center">
          <PDFIcon />
          <SH3 className="ml-1">
            {i18n.t('brandShareDetails.process.booklet')}
          </SH3>
        </XStack>
      ),
      shareDetails: content?.applicationProcess?.pdfLink,
      eventPayload: { item_name: 'brands_share_material_pdf' },
      isRender: !!content?.applicationProcess?.pdfLink,
      onPress: () => onShare(content?.applicationProcess?.pdfLink),
    },
  ]

  if (isLoading) {
    return <PdfDownloadLoader />
  }

  return (
    <>
      <ScrollView
        className="flex-1 bg-white px-3"
        showsVerticalScrollIndicator={false}
      >
        <Stack className="h-[250] w-full items-center justify-center">
          {Platform.OS === 'web' ? <Lottie animationData={require('../../../assets/lottie/MessageSent.json')} /> : 
          <LottieView
            autoPlay
            source={require('../../../assets/lottie/MessageSent.json')}
          /> }
        </Stack>
        <Stack>
          <H4 className="text-grays-60 text-center">
            {params?.customerName
              ? i18n.t('brandShareDetails.headingCustomer', {
                  bankName: content?.name,
                })
              : i18n.t('brandShareDetails.headingNoCustomer', {
                  bankName: content?.name,
                })}
          </H4>
          <H5 className="text-center">{params?.customerName || ''}</H5>
          {params?.customerId && params?.leadId && (
            <B2 className="text-grays-60 my-2 text-center">
              {i18n.t('brandShareDetails.leadId', { leadID: params?.leadId })}
            </B2>
          )}
        </Stack>
        <Stack className="rounded bg-[#E1EFFF] p-2">
          <B2 className="text-center">
            {params?.customerName
              ? i18n.t('brandShareDetails.instructionCustomer')
              : i18n.t('brandShareDetails.instructionNoCustomer')}
          </B2>
        </Stack>
        <Stack className="mb-3 mt-3">
          <B2>{i18n.t('brandShareDetails.helpText')}</B2>
          {shareDetails.map((value) =>
            value.isRender ? (
              <XStack
                className="border-grays-30 bg-grays-20 mt-3 items-center justify-between rounded border p-2"
                key={value.id}
              >
                {value.iconDetails}
                {Platform.OS !== 'web' && (
                  <Pressable
                    className="border-grays-30 bg-grays-20 active:bg-grays-10 items-center justify-center rounded-md border p-1"
                    onPress={value.onPress}
                    eventName="click_action"
                    eventPayload={value.eventPayload}
                  >
                    <Icon type="Share" color={zetColors.primary[100]} />
                  </Pressable>
                )}
              </XStack>
            ) : (
              <></>
            )
          )}
        </Stack>
      </ScrollView>
      <XStack className="border-grays-30 h-[75] justify-between border-t bg-white px-3 py-2">
        {routeName !== 'category' && (
          <Button
            size={'lg'}
            outlined
            textStyle="text-primary-100"
            className="mr-2 flex-1"
            onPress={handleLeadProgress}
            eventName="click_action"
            eventPayload={{
              item_name:
                'brand_list_recommendation_brand_share_check_lead_status',
            }}
          >
            {i18n.t('brandDetails.viewLead')}
          </Button>
        )}
        <Button
          size={'lg'}
          className="flex-1"
          onPress={handleReshare}
          eventName="click_action"
          eventPayload={{
            item_name: 'brand_list_recommendation_brand_share_reshare_link',
          }}
        >
          {routeName !== 'category'
            ? i18n.t('brandShareDetails.reshareLink')
            : i18n.t('brandShareDetails.reshareLinkWithCustomer')}
        </Button>
      </XStack>
      <WebShare />
    </>
  )
}

export default BrandShareDetails
